class LaasaProductChart extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    const trigger = this.querySelector('.laasa-pdp-chart-link');
    const dialog = this.querySelector('dialog');
    if (!trigger || !dialog) return;
    trigger.addEventListener('click', () => dialog.showModal(), options);
    this.querySelector('[data-chart-close]')?.addEventListener('click', () => dialog.close(), options);
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }, options);
    dialog.addEventListener('close', () => trigger.focus({ preventScroll: true }), options);
  }
  disconnectedCallback() { this.querySelector('dialog')?.close(); this.controller?.abort(); }
}

class LaasaProductDescription extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    const source = this.querySelector('.laasa-pdp-description-source');
    if (!source) return;
    this.dataset.ready = 'true';
    // The previous Tabs app leaves inactive panels inert and aria-hidden. CSS
    // cannot make inert content selectable. Our native details now own these
    // sections, so retain the product content without the old app's wrappers.
    const unwrapLegacyTabs = root => {
      root.querySelectorAll('.station-tabs-tablist, .station-tabs-local-above, .station-tabs-global-above, .station-tabs-local-below, .station-tabs-global-below').forEach(node => node.remove());
      for (const panel of [...root.querySelectorAll('.station-tabs-tabpanel, .station-tabs-tabcontent')].reverse()) {
        panel.replaceWith(...panel.childNodes);
      }
    };
    unwrapLegacyTabs(source);
    const sections = [
      ['description', 'Product description', 'Contact us for more details about this product.'],
      ['info', 'Product Info.', 'Contact us for the specifications of this product.'],
      ['care', 'Wash & Care', 'Care instructions are not listed for this product. Please check the garment care label.'],
      ['size', 'Size Guide', 'A size chart is not available for this product. Contact us for sizing help.']
    ];
    const bodies = new Map(sections.map(([key]) => [key, document.createElement('div')]));
    const headingKey = text => {
      const name = text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      if (/^(product )?description$/.test(name) || name === 'overview') return 'description';
      if (/^(product info|product information|product details|specifications|details|fabric details|material composition)$/.test(name)) return 'info';
      if (/^(wash care|wash and care|washcare|care|care instructions|washing instructions)$/.test(name)) return 'care';
      if (/^(size guide|size chart|sizing|size fit|size and fit)$/.test(name)) return 'size';
      return null;
    };
    let current = 'description';
    // Unwrap only containers that contain section headings, keeping lists, tables,
    // images and arbitrary product copy intact. Merge repeat headings by category.
    const visit = node => {
      const element = node.nodeType === 1;
      if (element && node.tagName === 'DETAILS') {
        const summary = [...node.children].find(child => child.tagName === 'SUMMARY');
        const key = summary && headingKey(summary.textContent);
        if (key) {
          current = key;
          for (const child of [...node.childNodes]) if (child !== summary) visit(child);
          return;
        }
      }
      if (element && /^H[1-6]$/.test(node.tagName)) {
        const key = headingKey(node.textContent);
        if (key) { current = key; return; }
      }
      if (element && /^(P|DIV)$/.test(node.tagName) && node.children.length === 1 && /^(STRONG|B)$/.test(node.firstElementChild.tagName)) {
        const key = headingKey(node.textContent);
        if (key) { current = key; return; }
      }
      if (element && /^(DIV|SECTION|ARTICLE)$/.test(node.tagName) && node.querySelector('h1,h2,h3,h4,h5,h6,details, p>strong, p>b')) {
        for (const child of [...node.childNodes]) visit(child);
        return;
      }
      bodies.get(current).append(node);
    };
    for (const node of [...source.childNodes]) visit(node);
    // Product-specific metafields and chart supplement, never replace, description.
    for (const extra of this.querySelectorAll('template[data-laasa-extra]')) {
      const body = bodies.get(extra.dataset.laasaExtra);
      if (body && !body.textContent.trim() && !body.querySelector('img,table,video')) body.append(extra.content.cloneNode(true));
    }
    const fragment = document.createDocumentFragment();
    for (const [key, title, missing] of sections) {
      const details = document.createElement('details');
      const summary = document.createElement('summary');
      summary.textContent = title;
      const body = bodies.get(key);
      unwrapLegacyTabs(body);
      body.className = 'rte';
      if (!body.textContent.trim() && !body.querySelector('img,table,video,iframe')) {
        const note = document.createElement('p');
        const link = document.createElement('a');
        link.href = '/pages/contact';
        link.textContent = missing;
        note.append(link);
        body.append(note);
        details.dataset.contentMissing = 'true';
      }
      if (key === 'description') details.open = true;
      details.append(summary, body);
      fragment.append(details);
    }
    this.replaceChildren(fragment);
  }
}
if (!customElements.get('laasa-product-chart')) customElements.define('laasa-product-chart', LaasaProductChart);
if (!customElements.get('laasa-product-description')) customElements.define('laasa-product-description', LaasaProductDescription);
